import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import { TI_OUTCOMES } from '../../treatment-intel/mockData';
import type { TiCallSubject, TiOutcomeGroup } from '../../treatment-intel/types';

export interface CallOutcomeModalProps {
  open: boolean;
  subject: TiCallSubject;
  onClose: () => void;
  onSave?: (outcomeKey: string) => void;
}

const GROUP_LABEL: Record<TiOutcomeGroup, string> = {
  reached: 'You reached them',
  'not-reached': 'You didn’t reach them',
  permanent: 'Permanent',
};


/**
 * Call-outcome modal (spec 07 board 14, §4.9). Logs what already happened on the
 * practice phone — it never dials. Eight outcomes in three groups; every option
 * states its consequence on its own row; an outcome is required before the row
 * can leave the list.
 */
export function CallOutcomeModal({ open, subject, onClose, onSave }: CallOutcomeModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [callBackDate, setCallBackDate] = useState('');
  const [error, setError] = useState(false);

  const chosen = useMemo(() => TI_OUTCOMES.find((o) => o.key === selected) ?? null, [selected]);

  const whatNext =
    chosen == null
      ? 'Choose an outcome and its consequence appears here.'
      : chosen.key === 'booked'
        ? `${subject.name.split(' ')[0] ?? 'They'} comes off this list. The Treatment Coordinator stops messaging about ${subject.caseText}, and Appointment Reminders takes over from the booking. The call is credited to a human, not to the agent.`
        : chosen.consequence;

  const handleSave = () => {
    if (selected == null) {
      setError(true);
      return;
    }
    if (chosen?.needsDate && callBackDate === '') {
      setError(true);
      return;
    }
    onSave?.(selected);
  };

  const renderGroup = (g: TiOutcomeGroup) => (
    <div>
      <div className="mb-1.5 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        {GROUP_LABEL[g]}
      </div>
      <div className="grid gap-1.5">
        {TI_OUTCOMES.filter((o) => o.group === g).map((o) => {
          const on = o.key === selected;
          return (
            <div key={o.key}>
              <button
                type="button"
                onClick={() => {
                  setSelected(o.key);
                  setError(false);
                }}
                className={cn(
                  'flex w-full items-start gap-2.5 rounded-[var(--r-md)] border px-3 py-2.5 text-left transition',
                  on
                    ? o.destructive
                      ? 'border-[var(--alert-red-line)] bg-[var(--alert-red-tint)]'
                      : 'border-[var(--brand-indigo)] bg-[color-mix(in_srgb,var(--brand-indigo)_8%,transparent)]'
                    : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:bg-[var(--surface-sunken)]',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2',
                    on
                      ? o.destructive
                        ? 'border-[var(--alert-red)] bg-[var(--alert-red)]'
                        : 'border-[var(--brand-indigo)] bg-[var(--brand-indigo)]'
                      : 'border-[var(--border-strong)]',
                  )}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-[13px] font-semibold',
                      o.destructive ? 'text-[var(--alert-red)]' : 'text-[var(--text-primary)]',
                    )}
                  >
                    {o.label}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-[var(--text-muted)]">
                    {o.consequence}
                  </span>
                </span>
              </button>
              {on && o.needsDate ? (
                <input
                  type="date"
                  value={callBackDate}
                  onChange={(e) => {
                    setCallBackDate(e.target.value);
                    setError(false);
                  }}
                  className="mt-1.5 w-full rounded-[var(--r-sm)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="How did the call go?"
      className="w-[min(820px,calc(100vw-32px))]"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save the outcome
          </Button>
        </>
      }
    >
      <div className="-mt-1 mb-3 text-[12.5px] text-[var(--text-secondary)]">
        <span className="font-semibold text-[var(--text-primary)]">{subject.name}</span> ·{' '}
        <span className="font-[var(--font-mono)]">{subject.caseText}</span> ·{' '}
        <span className="text-[var(--text-muted)]">{subject.when}</span>
      </div>

      {/* Two columns (board 14): you reached them on the left; you didn't +
          permanent on the right. The panels below span full width. */}
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <div>{renderGroup('reached')}</div>
        <div className="space-y-4">
          {renderGroup('not-reached')}
          {renderGroup('permanent')}
        </div>
      </div>

      {/* what happens next */}
      <div className="mt-5 rounded-[var(--r-md)] border border-[var(--money-green-line)] bg-[var(--money-green-tint)] px-3 py-2.5">
        <div className="font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--money-green)]">
          What happens next
        </div>
        <div className="mt-1 text-[12px] leading-[1.5] text-[var(--text-secondary)]">{whatNext}</div>
      </div>

      {/* note */}
      <div className="mt-3">
        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
          Anything worth remembering
        </div>
        <textarea
          rows={2}
          placeholder="Goes on the case, not into a message."
          className="w-full resize-none rounded-[var(--r-sm)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
        />
        <p className="mt-1.5 text-[11px] leading-[1.5] text-[var(--text-muted)]">
          This is the only thing the agent cannot find out on its own — opens, clicks and replies
          arrive by themselves; what was said on the phone does not.
        </p>
      </div>

      {error ? (
        <p className="mt-2 text-[12px] font-semibold text-[var(--alert-red)]">
          {chosen?.needsDate ? 'Pick a call-back date first.' : 'Choose an outcome first — a call with no outcome is worse than no call.'}
        </p>
      ) : null}
    </Modal>
  );
}
